import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, FolderGit2, Radar, Bug, FileText, Settings, LogOut, HelpCircle, Menu, X, ShieldCheck, Plug } from "lucide-react";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import VerifyBanner from "./VerifyBanner";
import GuidedTour from "./GuidedTour";
import { useAuth } from "../../lib/auth";

const nav = [
  { to: "/app", label: "Dashboard", Icon: LayoutDashboard, end: true },
  { to: "/app/projects", label: "Projects", Icon: FolderGit2 },
  { to: "/app/scans", label: "Scans", Icon: Radar },
  { to: "/app/findings", label: "Findings", Icon: Bug },
  { to: "/app/reports", label: "Reports", Icon: FileText },
  { to: "/app/integrations", label: "Integrations", Icon: Plug },
  { to: "/app/settings", label: "Settings", Icon: Settings },
];

export default function AppShell({ children, title, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(false);

  return (
    <div className="min-h-screen bg-paper text-ink flex" data-testid="app-shell">
      {/* Sidebar */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-40 w-64 bg-panel text-ink transform transition-transform md:transform-none ${
          mobile ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-line">
          <Link to="/app" className="flex items-center" onClick={() => setMobile(false)}>
            <Logo invert size={24} />
          </Link>
          <button className="md:hidden text-ink/70" onClick={() => setMobile(false)} aria-label="Close menu">
            <X size={20} />
          </button>
        </div>
        <nav className="px-3 py-4 space-y-1">
          {nav.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setMobile(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "bg-ink/10 text-ink" : "text-ink/70 hover:bg-ink/5 hover:text-ink"
                }`
              }
              data-testid={`sidebar-${label.toLowerCase()}`}
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
          {user?.role === "superadmin" && (
            <NavLink
              to="/admin"
              onClick={() => setMobile(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors mt-2 border-t border-line pt-4 ${
                  isActive ? "bg-gold/15 text-gold" : "text-gold/80 hover:bg-gold/10 hover:text-gold"
                }`
              }
              data-testid="sidebar-admin"
            >
              <ShieldCheck size={18} /> Owner Console
            </NavLink>
          )}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-line">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-gold/20 flex items-center justify-center text-ink text-sm font-semibold">
              {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-ink/50 truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            className="flex items-center gap-2 text-xs text-ink/60 hover:text-ink"
            data-testid="sidebar-logout"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 md:ml-0">
        <VerifyBanner />
        <header className="h-16 sticky top-0 bg-paper/90 backdrop-blur border-b border-line z-30 flex items-center px-4 md:px-8 gap-3" data-testid="app-topbar">
          <button className="md:hidden p-2" onClick={() => setMobile(true)} aria-label="Open menu" data-testid="open-sidebar">
            <Menu size={22} />
          </button>
          <h1 className="font-display text-xl md:text-2xl truncate" data-testid="page-title">{title}</h1>
          <div className="ml-auto flex items-center gap-2">
            {actions}
            <ThemeToggle />
            <button
              onClick={() => { localStorage.removeItem("argus.tour.completed-v1"); window.dispatchEvent(new CustomEvent("argus:start-tour")); }}
              className="p-2 text-ink/60 hover:text-gold"
              aria-label="Replay guided tour"
              title="Replay guided tour"
              data-testid="topbar-help"
            >
              <HelpCircle size={18} />
            </button>
          </div>
        </header>
        <main className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="app-main">
          {children}
        </main>
      </div>

      {mobile && <div className="fixed inset-0 bg-black/30 z-30 md:hidden" onClick={() => setMobile(false)} />}
      <GuidedTour />
    </div>
  );
}
